import type { CarddavAccess, CarddavCreated } from "@kontaktfix/shared";
import { Button, Card, CardHeader, Dialog, Skeleton } from "@kontaktfix/ui";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDate, formatRelative } from "@/lib/i18n";
import { CopyValue, download } from "@/pages/connect-own-app";

/** Person page, administrators: read-only CardDAV access for phones without Outlook (issue #29). */
export function CarddavCard({ userId, name }: { userId: string; name: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const key = ["carddav", userId] as const;
  const { data, isPending } = useQuery({
    queryKey: key,
    queryFn: () => api.get<CarddavAccess>(`/api/people/${userId}/carddav`),
  });
  const [created, setCreated] = useState<CarddavCreated | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      setCreated(await api.post<CarddavCreated>(`/api/people/${userId}/carddav`));
      await client.invalidateQueries({ queryKey: key });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setRemoving(false);
    try {
      await api.delete(`/api/people/${userId}/carddav`);
      setCreated(null);
      toast.success(t("carddav.removed"));
      await client.invalidateQueries({ queryKey: key });
    } catch (err) {
      toastError(err);
    }
  };

  const access = data?.access;
  return (
    <Card>
      <CardHeader title={t("carddav.title")} description={t("carddav.description")} />
      <div className="grid gap-3 border-t border-border px-5 py-4 text-[13px]">
        {isPending ? (
          <Skeleton className="h-16" />
        ) : created ? (
          <>
            <p className="text-fg-muted">{t("carddav.createdText")}</p>
            <dl className="grid gap-2 sm:grid-cols-[8rem_1fr] sm:items-center">
              <dt className="text-fg-muted">{t("carddav.server")}</dt>
              <dd className="min-w-0">
                <CopyValue value={created.url} />
              </dd>
              <dt className="text-fg-muted">{t("carddav.username")}</dt>
              <dd className="min-w-0">
                <CopyValue value={created.username} />
              </dd>
              <dt className="text-fg-muted">{t("carddav.password")}</dt>
              <dd className="min-w-0">
                <CopyValue value={created.password} />
              </dd>
            </dl>
            <div>
              <Button
                size="sm"
                onClick={() =>
                  download(
                    `Kontaktfix-${name.replace(/[^\p{L}\p{N}_-]+/gu, "-")}.mobileconfig`,
                    created.mobileconfig,
                    "application/x-apple-aspen-config",
                  )
                }
              >
                <DownloadSimpleIcon />
                {t("carddav.profile")}
              </Button>
            </div>
            <p className="text-fg-muted">{t("carddav.setup")}</p>
          </>
        ) : access ? (
          <p className="text-fg-muted">
            {t("carddav.active", {
              username: access.username,
              date: formatDate(access.createdAt),
              used: access.lastUsedAt ? formatRelative(access.lastUsedAt) : t("common.never"),
            })}
          </p>
        ) : (
          <p className="text-fg-muted">{t("carddav.none")}</p>
        )}
        <p className="text-[12.5px] text-fg-muted">{t("carddav.twice")}</p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={access ? "secondary" : "primary"}
            onClick={create}
            disabled={busy || isPending}
          >
            {access ? t("carddav.renew") : t("carddav.create")}
          </Button>
          {access && (
            <Button size="sm" variant="danger-ghost" onClick={() => setRemoving(true)}>
              {t("carddav.remove")}
            </Button>
          )}
        </div>
      </div>
      <Dialog
        open={removing}
        onOpenChange={setRemoving}
        title={t("carddav.removeTitle", { name })}
        description={t("carddav.removeText")}
        footer={
          <>
            <Button onClick={() => setRemoving(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={remove}>
              {t("carddav.remove")}
            </Button>
          </>
        }
      />
    </Card>
  );
}
